import React from 'react';
import classnames from 'classnames';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react';

import style from './TabsNavigationPage.module.scss';

import type { TranslationValues } from '@chaika/i18n/types';

import { RightMenu } from '@components/RightMenu';
import { translate } from '@chaika/i18n/helper';
import { useTabsNavigationPage } from './hooks/useTabsNavigationPage';

export interface PagesParams<N = string> {
    title: TranslationValues;
    name: N;
    page: React.ComponentType<any> | React.FunctionComponent;
    pageProps?: object;
    dataTest?: string;
}

export interface TabsNavigationPageProps {
    tabTitle?: TranslationValues | string;
    commonTitle?: string;
    banner?: React.ReactNode;
    additionalClassName?: string;
    /** Disables the small-screen negative margin that offsets the page-shell top padding. */
    withoutMobileOffset?: boolean;
    withoutRightContent?: boolean;
    withoutRightMenu?: boolean;
    rightContent?: () => React.ReactNode;
    rightMenuContent?: () => React.ReactNode;
    onRightMenuOutsideClick?(): void;
}

interface Props extends TabsNavigationPageProps {
    pages: PagesParams[];
    onTabSelect(index: number): void;
}

export const TabsNavigationPage = observer(
    ({
        pages,
        tabTitle: tabTitleProp,
        commonTitle,
        banner,
        additionalClassName,
        withoutMobileOffset,
        withoutRightContent = true,
        withoutRightMenu = true,
        rightContent,
        rightMenuContent,
        onRightMenuOutsideClick,
        onTabSelect,
    }: Props) => {
        const { isOnLoad, selectedPageParam, tabTitle } = useTabsNavigationPage({
            pages,
            tabTitle: tabTitleProp,
            onTabSelect,
        });

        const renderContent = (): React.ReactNode => {
            if (isOnLoad) {
                return 'no content';
            }

            const SelectedPage = selectedPageParam?.page;

            return SelectedPage ? <SelectedPage {...selectedPageParam?.pageProps} /> : selectedPageParam?.title || '';
        };

        const page = (
            <div
                className={classnames([
                    style.root,
                    withoutMobileOffset && style.root_withoutMobileOffset,
                    additionalClassName,
                ])}
            >
                {tabTitle && (
                    <Helmet>
                        <title>{translate(tabTitle as TranslationValues)}</title>
                    </Helmet>
                )}
                {commonTitle && <div className={style.title}>{commonTitle}</div>}
                {renderContent()}
                {!withoutRightContent && <div className={style.filterMenu}>{rightContent?.()}</div>}
                {!withoutRightMenu && (
                    <RightMenu
                        isShowing={!withoutRightMenu}
                        onClickOutside={onRightMenuOutsideClick}
                    >
                        {rightMenuContent?.()}
                    </RightMenu>
                )}
            </div>
        );

        if (!banner) {
            return page;
        }

        return (
            <div className={style.bannerLayout}>
                <div className={style.banner}>{banner}</div>
                {page}
            </div>
        );
    },
);
